import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import HeaderBar from "../components/layout/Headerbar";
import NavBar from "../components/layout/NavBar";
import LargeButton from "../components/input/LargeButton";
import SmallButton from "../components/input/SmallButton";
import Modal from "../components/layout/Modal";
import {
  useApiCustomPoi,
  apiCreateCustomPoi,
  apiUpdateCustomPoi,
  apiGetCustomPoiUploadUrl,
  apiUploadFile,
  apiExtractAudioDuration,
  apiUploadCustomPoiFile,
} from "../api/customPoi";
import { useApiPersonas } from "../api/api";
import { CustomPoiCreatePayload, CustomPoiUpdatePayload } from "../model/customPoi";
import { PersonaModel } from "../model/persona";
import AudioPicker from "../components/input/AudioPicker";
import ImagePicker from "../components/input/ImagePicker";
import "./CustomPoiEdit.css";

function CustomPoiEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  // All hooks must be called at the top level, before any conditional logic
  const isNew = id === "new";
  const poiId = isNew ? null : (id && id !== "NaN" ? parseInt(id, 10) : null);
  const isValidPoiId = poiId !== null && !isNaN(poiId);

  // API hooks (must be called before any early returns)
  const existingPoi = useApiCustomPoi(isValidPoiId ? poiId!.toString() : "");
  const personas = useApiPersonas();

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [transcript, setTranscript] = useState("");
  const [selectedPersonaId, setSelectedPersonaId] = useState<number | null>(null);
  const [coordinates, setCoordinates] = useState("");
  const [language, setLanguage] = useState("en");
  const [tags, setTags] = useState("");

  // File state
  const [selectedAudioFile, setSelectedAudioFile] = useState<File | null>(null);
  const [selectedThumbnailFile, setSelectedThumbnailFile] = useState<File | null>(null);
  const [audioUploadProgress, setAudioUploadProgress] = useState(0);
  const [thumbnailUploadProgress, setThumbnailUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Duration extraction state
  const [audioS3Key, setAudioS3Key] = useState<string>("");
  const [extractedDuration, setExtractedDuration] = useState<number | null>(null);
  const [extractionStatus, setExtractionStatus] = useState<"idle" | "uploading" | "extracting" | "success" | "error">("idle");
  const [extractionError, setExtractionError] = useState<string>("");

  // UI state
  const [pendingSave, setPendingSave] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Upload and extract duration function (must be defined before useEffect)
  const uploadAudioFileAndExtractDuration = useCallback(async (file: File): Promise<{ s3Key: string; duration: number }> => {
    setExtractionStatus("uploading");
    setExtractionError("");

    try {
      // Upload the audio file directly to backend
      const uploadResponse = await apiUploadCustomPoiFile(file, 'audio', selectedPersonaId!, isNew ? undefined : (isValidPoiId ? poiId! : undefined));

      if (!uploadResponse.data?.objectKey) {
        throw new Error("Upload failed - no S3 key returned");
      }

      const s3Key = uploadResponse.data.objectKey;
      setAudioS3Key(s3Key);
      setExtractionStatus("extracting");

      // Extract duration from the uploaded file
      const durationResponse = await apiExtractAudioDuration(s3Key, 'PUBLIC');

      if (!durationResponse.data?.success) {
        throw new Error("Failed to extract audio duration");
      }

      const duration = durationResponse.data.duration;

      setExtractedDuration(duration);
      setExtractionStatus("success");

      return { s3Key, duration };

    } catch (error: any) {
      console.error("Failed to upload audio and extract duration:", error);
      setExtractionStatus("error");
      setExtractionError(error.message || "Failed to process audio file");
      throw error;
    }
  }, [isNew, poiId, isValidPoiId, selectedPersonaId]);

  // Initialize form with existing data (must be called before conditional logic)
  useEffect(() => {
    if (!isNew && existingPoi.data) {
      const poi = existingPoi.data;
      setTitle(poi.title);
      setDescription(poi.description);
      setTranscript(poi.transcript);
      setSelectedPersonaId(poi.personaId);
      setCoordinates(`${poi.latitude}, ${poi.longitude}`);
      setLanguage(poi.language || "en");
      setTags(poi.tags || "");
    }
  }, [isNew, existingPoi.data]);

  // Handle invalid ID parameter
  useEffect(() => {
    if (!id) {
      console.warn("CustomPoiEdit: No ID parameter provided, redirecting to list");
      navigate("/custom-pois");
    }
  }, [id, navigate]);

  // Effect to upload audio file when persona is selected (if audio file was selected first)
  useEffect(() => {
    console.log("useEffect triggered:", {
      hasSelectedAudioFile: !!selectedAudioFile,
      selectedPersonaId,
      extractionStatus
    });

    if (selectedAudioFile && selectedPersonaId && extractionStatus === "idle") {
      console.log("Triggering upload and extraction for selected file:", selectedAudioFile.name);
      uploadAudioFileAndExtractDuration(selectedAudioFile);
    }
  }, [selectedPersonaId, selectedAudioFile, extractionStatus, uploadAudioFileAndExtractDuration]);

  // Don't render if id is undefined
  if (!id) {
    return null;
  }

  const validateForm = (): boolean => {
    if (!title.trim()) {
      setError("Title is required");
      return false;
    }
    if (!description.trim()) {
      setError("Description is required");
      return false;
    }
    if (!transcript.trim()) {
      setError("Transcript is required");
      return false;
    }
    if (!selectedPersonaId) {
      setError("Persona selection is required");
      return false;
    }
    if (!coordinates.trim()) {
      setError("Coordinates are required");
      return false;
    }

    const coords = coordinates.split(',').map((coord: string) => coord.trim());
    if (coords.length !== 2) {
      setError("Coordinates must be in format: latitude, longitude");
      return false;
    }

    const lat = parseFloat(coords[0]);
    const lng = parseFloat(coords[1]);
    if (isNaN(lat) || lat < -90 || lat > 90) {
      setError("Invalid latitude (must be between -90 and 90)");
      return false;
    }
    if (isNaN(lng) || lng < -180 || lng > 180) {
      setError("Invalid longitude (must be between -180 and 180)");
      return false;
    }

    setError(null);
    return true;
  };

  const uploadFile = async (
    file: File,
    type: 'audio' | 'thumbnail',
    poiId?: number
  ): Promise<string> => {
    try {
      // Use the new direct upload API
      const uploadResponse = await apiUploadCustomPoiFile(file, type, selectedPersonaId!, poiId);

      if (!uploadResponse.data?.objectKey) {
        throw new Error('Upload failed - no S3 key returned');
      }

      return uploadResponse.data.objectKey;
    } catch (error: any) {
      // Surface backend error message when available
      const backendMessage =
        error?.error?.response?.data?.message ||
        error?.response?.data?.message ||
        error?.message;
      console.error(`Failed to upload ${type}:`, error);
      throw new Error(backendMessage || `Failed to upload ${type}`);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    setError(null);

    try {
      // For new POIs, we need to have uploaded the audio file and extracted duration already
      if (isNew && (!audioS3Key || !extractedDuration)) {
        throw new Error("Audio file must be uploaded and duration extracted before creating POI");
      }

      let thumbnailKey: string | undefined;

      // Upload thumbnail if selected
      if (selectedThumbnailFile) {
        thumbnailKey = await uploadFile(selectedThumbnailFile, 'thumbnail', isNew ? undefined : (isValidPoiId ? poiId! : undefined));
        setThumbnailUploadProgress(100);
      }

      const coords = coordinates.split(',').map((coord: string) => coord.trim());
      const lat = parseFloat(coords[0]);
      const lng = parseFloat(coords[1]);

      const baseData = {
        title: title.trim(),
        description: description.trim(),
        transcript: transcript.trim(),
        personaId: selectedPersonaId!,
        latitude: lat,
        longitude: lng,
        language: language || undefined,
        tags: tags.trim() || undefined,
      };

      if (isNew) {
        const createData: CustomPoiCreatePayload = {
          ...baseData,
          thumbnail: thumbnailKey,
          audioFileKey: audioS3Key, // Use the pre-uploaded S3 key
          thumbnailFileKey: thumbnailKey,
          audioLength: extractedDuration!, // Use the pre-extracted duration
        };

        const response = await apiCreateCustomPoi(createData);
        if (response.data) {
          navigate(`/custom-pois/${response.data.id}`);
        }
      } else {
        if (!isValidPoiId) {
          throw new Error("Invalid POI ID. Cannot update POI.");
        }
        const updateData: CustomPoiUpdatePayload = {
          ...baseData,
          ...(thumbnailKey && { thumbnail: thumbnailKey }),
        };
        await apiUpdateCustomPoi(poiId!, updateData);
        existingPoi.refresh();
      }

      setPendingSave(false);
    } catch (error: any) {
      console.error("Failed to save Custom POI:", error);
      setError(error.message || "Failed to save Custom POI");
    } finally {
      setIsLoading(false);
      setIsUploading(false);
    }
  };

  const handleAudioSelected = async (file: File) => {
    console.log("handleAudioSelected called with file:", file.name, file.type);
    setSelectedAudioFile(file);

    // Only start the upload if we have a persona selected
    if (selectedPersonaId) {
      try {
        console.log("Starting upload and extraction process...");
        await uploadAudioFileAndExtractDuration(file);
        setPendingSave(true);
        console.log("Upload and extraction completed successfully");
      } catch (error) {
        console.error("Failed to upload audio and extract duration:", error);
        // Error is already handled in uploadAudioFileAndExtractDuration
      }
    } else {
      console.log("No persona selected yet, waiting for persona selection...");
      setPendingSave(true);
      // The useEffect will trigger the upload when persona is selected
    }
  };

  const handleThumbnailSelected = (file: File) => {
    setSelectedThumbnailFile(file);
    setPendingSave(true);
  };

  const getPersonaOptions = () => {
    if (!personas.data) return [];
    return personas.data.map((persona: PersonaModel) => ({
      value: persona.id.toString(),
      label: persona.name,
      persona,
    }));
  };

  // Handle invalid POI ID for existing POIs
  if (!isNew && !isValidPoiId) {
    return (
      <div className="app">
        <div className="error-message">Invalid POI ID</div>
      </div>
    );
  }

  if (!isNew && existingPoi.loading) {
    return (
      <div className="app">
        <div className="loading">Loading Custom POI...</div>
      </div>
    );
  }

  const isUserForbidden = existingPoi.error?.response?.status === 403;

  return (
    <div className="app">
      <HeaderBar>
        <h1
          style={{
            display: "inline-block",
            marginLeft: 20,
            verticalAlign: "top",
            paddingTop: 13.5,
            fontWeight: 600,
          }}
        >
          {isNew ? "Create Custom POI" : "Edit Custom POI"}
        </h1>
        <div
          style={{
            display: "inline-flex",
            right: 0,
            position: "absolute",
            boxSizing: "border-box",
            padding: 15,
          }}
        >
          <SmallButton
            text="Cancel"
            onClick={() => navigate("/custom-pois")}
            isOutlineOnly={true}
            className="cancel-btn"
            icon={null}
          />
          <LargeButton
            text={isNew ? "Create POI" : "Save Changes"}
            onClick={() => handleSave()}
            color="#34A853"
            disabled={!pendingSave || isLoading || isUploading}
            icon={null}
          />
          {!isNew && existingPoi.data?.status === 'draft' && (
            <LargeButton
              text="Publish"
              onClick={() => setShowPublishDialog(true)}
              color="#4285F4"
              icon={null}
            />
          )}
        </div>
      </HeaderBar>

      <NavBar selectedIndex={3} isUserForbidden={isUserForbidden} />

      <div className="content" style={{ paddingLeft: 16, paddingRight: 16 }}>
        {!isUserForbidden ? (
          <div className="poi-edit-container">
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <div className="form-grid">
              <div className="form-section">
                <h3>Basic Information</h3>

                <div className="form-group">
                  <label htmlFor="title">Title *</label>
                  <input
                    id="title"
                    type="text"
                    value={title}
                    onChange={(e) => {
                      setTitle(e.target.value);
                      setPendingSave(true);
                    }}
                    placeholder="Enter POI title"
                    maxLength={255}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description *</label>
                  <textarea
                    id="description"
                    value={description}
                    onChange={(e) => {
                      setDescription(e.target.value);
                      setPendingSave(true);
                    }}
                    placeholder="Describe the POI"
                    rows={3}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="transcript">Transcript *</label>
                  <textarea
                    id="transcript"
                    value={transcript}
                    onChange={(e) => {
                      setTranscript(e.target.value);
                      setPendingSave(true);
                    }}
                    placeholder="Full transcript of the audio content"
                    rows={6}
                  />
                </div>
              </div>

              <div className="form-section">
                <h3>Location & Persona</h3>

                <div className="form-group">
                  <label htmlFor="persona">Persona *</label>
                  <select
                    id="persona"
                    value={selectedPersonaId?.toString() || ""}
                    onChange={(e) => {
                      setSelectedPersonaId(parseInt(e.target.value));
                      setPendingSave(true);
                    }}
                  >
                    <option value="">Select a persona</option>
                    {getPersonaOptions().map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="coordinates">Coordinates (Lat, Lng) *</label>
                  <input
                    id="coordinates"
                    type="text"
                    value={coordinates}
                    onChange={(e) => {
                      setCoordinates(e.target.value);
                      setPendingSave(true);
                    }}
                    placeholder="e.g. 65.01254333944534, 25.469735854095664"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="language">Language</label>
                    <select
                      id="language"
                      value={language}
                      onChange={(e) => {
                        setLanguage(e.target.value);
                        setPendingSave(true);
                      }}
                    >
                      <option value="en">English</option>
                      <option value="es">Spanish</option>
                      <option value="fr">French</option>
                      <option value="de">German</option>
                      <option value="it">Italian</option>
                      <option value="pt">Portuguese</option>
                      <option value="ru">Russian</option>
                      <option value="ja">Japanese</option>
                      <option value="ko">Korean</option>
                      <option value="zh">Chinese</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="tags">Tags</label>
                  <input
                    id="tags"
                    type="text"
                    value={tags}
                    onChange={(e) => {
                      setTags(e.target.value);
                      setPendingSave(true);
                    }}
                    placeholder="Comma-separated tags (e.g. history, culture, food)"
                  />
                </div>
              </div>

              <div className="form-section">
                <h3>Media Upload</h3>

                <div className="upload-section">
                  <div className="upload-group">
                    <label>Audio File (MP3) *</label>
                    <AudioPicker onValidAudioSelected={handleAudioSelected} />
                    {selectedAudioFile && (
                      <div className="file-info">
                        Selected: {selectedAudioFile.name}
                      </div>
                    )}

                    {/* Duration extraction status */}
                    {extractionStatus !== "idle" && (
                      <div className="extraction-status">
                        {extractionStatus === "uploading" && (
                          <div className="status-uploading">
                            <span className="spinner"></span>
                            Uploading audio file...
                          </div>
                        )}
                        {extractionStatus === "extracting" && (
                          <div className="status-extracting">
                            <span className="spinner"></span>
                            Extracting duration...
                          </div>
                        )}
                        {extractionStatus === "success" && extractedDuration !== null && (
                          <div className="status-success">
                            ✅ Duration: {Math.floor(extractedDuration / 60)}:{(extractedDuration % 60).toFixed(0).padStart(2, '0')}
                          </div>
                        )}
                        {extractionStatus === "error" && (
                          <div className="status-error">
                            ❌ {extractionError || "Failed to process audio file"}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Show message when file is selected but no persona chosen */}
                    {selectedAudioFile && !selectedPersonaId && extractionStatus === "idle" && (
                      <div className="extraction-status status-info">
                        📁 Audio file selected. Please choose a persona to start upload and duration extraction.
                      </div>
                    )}

                    {audioUploadProgress > 0 && extractionStatus !== "success" && (
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{ width: `${audioUploadProgress}%` }}
                        />
                      </div>
                    )}
                  </div>

                  <div className="upload-group">
                    <label>Thumbnail Image (Optional)</label>
                    <ImagePicker handleValidImageSelected={handleThumbnailSelected} />
                    {selectedThumbnailFile && (
                      <div className="file-info">
                        Selected: {selectedThumbnailFile.name}
                      </div>
                    )}
                    {thumbnailUploadProgress > 0 && (
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{ width: `${thumbnailUploadProgress}%` }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="forbidden-message">
            <h1>Insufficient Permissions</h1>
            <p>Please contact your administrator for access.</p>
          </div>
        )}
      </div>

      {/* Publish Confirmation Modal */}
      <Modal
        isOpen={showPublishDialog}
        onClose={() => setShowPublishDialog(false)}
      >
        <h3>Publish Custom POI</h3>
        <p>
          Are you sure you want to publish this Custom POI? Once published, it will be visible to users in search results.
        </p>
        <div className="modal-actions">
          <SmallButton
            text="Cancel"
            onClick={() => setShowPublishDialog(false)}
            isOutlineOnly={true}
            icon={null}
          />
          <LargeButton
            text="Publish"
            onClick={() => {
              // Handle publish logic here
              setShowPublishDialog(false);
            }}
            color="#34A853"
            icon={null}
          />
        </div>
      </Modal>
    </div>
  );
}

export default CustomPoiEdit;
